import { autorun, observable, action, computed } from 'mobx'
import client from '../../../helpers/client'
import { asNormalizedMaps, asNormalizedArrays } from '../../../helpers/json-normalizer'
import { addHours, format } from 'date-fns'
import flatMap from 'lodash/flatMap'


class Store {
  @observable ready = false
  @observable loading = true
  @observable franchise
  @observable eventCategories
  @observable packageGroups
  @observable eventTypes
  @observable activeDate
  @observable activeEventType
  @observable allowEditingActiveEventType = true

  constructor({ widgetStore }) {
    this.widgetStore = widgetStore
    this.host = widgetStore.host
  }

  async setup({ eventTypeId, eventTypeToken, packageGroupId, packageGroupToken, eventCategoryId, eventCategoryToken }) {
    this.loading = true
    await this.loadFranchise()

    if(eventTypeId) {
      await this.loadEventTypes({ token: eventTypeToken })
      this.activeEventType = this.eventTypes.get(`${eventTypeId}`)
      this.allowEditingActiveEventType = false
    }
    else if(packageGroupId) {
      await this.loadEventTypes({ packageGroupId, token: packageGroupToken })
    }
    else if(eventCategoryId) {
      await this.loadEventTypes({ eventCategoryId, token: eventCategoryToken })
    }
    else {
      await this.loadEventTypes({ token: null })
    }

    this.ready = true
    this.loading = false
  }

  loadFranchise() {
    return client.get(`${this.host}/api/v1/franchise`)
      .then(response => {
        const data = asNormalizedArrays(response.data)
        this.franchise = Object.values(data.franchise)[0]
      })
      .catch(error => (this.widgetStore.unrecoverableError = error))
  }

  loadEventTypes({ token, packageGroupId, eventCategoryId }) {
    let url = `${this.host}/api/v1/event_types?include=package_group&include=package_group.event_category`
    if(token) {
      url = `${url}&token=${token}`
    }
    console.log({ url, token })

    return client.get(url)
      .then(response => {
        const data = asNormalizedMaps(response.data)
        this.eventCategories = data.eventCategory
        this.packageGroups = data.packageGroup
        this.eventTypes = data.eventType

        if(eventCategoryId) {
          this.eventCategories = new Map(
            [...this.eventCategories].filter(([k, v]) => k == eventCategoryId )
          )
        }

        if(packageGroupId) {
          this.packageGroups = new Map(
            [...this.packageGroups].filter(([k, v]) => k == packageGroupId )
          )
        }
      })
      .catch(error => (this.widgetStore.unrecoverableError = error))
  }

  @computed get groupedPackageGroups() {
    const sortedEventCategories = Array.from(this.eventCategories)
      .map(([id, eventCategory]) => eventCategory)
      .sort((a, b) => a.attributes.position - b.attributes.position)

    return flatMap(sortedEventCategories, (eventCategory) => {
      return eventCategory.relationships.packageGroups.data
        .map(({ id }) => this.packageGroups.get(id))
        .filter(packageGroup => packageGroup)
        .sort((a, b) => a.attributes.position - b.attributes.position)
        .map(packageGroup => {
          return { eventCategory, packageGroup }
        })
    })
  }

  sortedEventTypesForPackageGroup(packageGroup) {
    const eventTypes = packageGroup.relationships.eventTypes.data
      .map(({ id }) => this.eventTypes.get(id))
      .filter(eventType => eventType)

    switch(packageGroup.attributes.packageSortPreference) {
      case "price":
        return  eventTypes.sort((a, b) => a.attributes.baseRateCents - b.attributes.baseRateCents)
      case "name":
        return eventTypes.sort((a, b) => a.attributes.name.localeCompare(b.attributes.name))
      case "manual":
        return eventTypes.sort((a, b) => a.attributes.position - b.attributes.position)
      default:
        return eventTypes
    }
  }

  @computed get calendarStartDate() {
    return addHours(new Date(), this.franchise.attributes.newBookingBufferHours)
  }


  @action checkAvailability() {
    this.loading = true

    const packageId = this.activeEventType.id
    const eventDate = format(this.activeDate, "yyyy-MM-dd")
    client.get(`${this.host}/api/v1/available_slots?package_id=${packageId}&event_date=${eventDate}`)
      .then(response => {
        const data = asNormalizedArrays(response.data)
        if(data.availabilitySlot && data.availabilitySlot.length > 0) {
          this.availabilityResult = true
        }
        else {
          this.availabilityResult = false
        }
        this.loading = false
      })
      .catch(error => (this.widgetStore.unrecoverableError = error))
  }

  @action progress({ activeDate, eventType }){
    if(activeDate) {
      this.activeDate = activeDate
    }

    if(eventType) {
      this.activeEventType = eventType
    }

    if(this.activeDate && this.activeEventType) {
      this.checkAvailability()
    }
  }

  @action startOver() {
    this.activeDate = null
    if(this.allowEditingActiveEventType) {
      this.activeEventType = null
    }
  }
}

export default Store
